import { useEffect, useMemo, useState } from 'react'
import { Clock } from 'lucide-react'
import Cal, { getCalApi } from '@calcom/embed-react'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import Seo from '@/components/Seo'
import { CAL_LINK } from '@/lib/booking'
import { useDarkMode } from '@/lib/theme'
import PublicBirds from '@/components/public/PublicBirds'
import { CHART_COLORS } from '@/lib/constants'

// Pulled from the app palette (src/lib/constants.js) so the booker reads on-brand.
const BRAND = CHART_COLORS.primary
const CAL_CSS_VARS = {
  light: { 'cal-brand': BRAND },
  dark: { 'cal-brand': BRAND },
}

// Build a friendly label for the visitor's timezone, e.g. "Central Daylight
// Time (CDT)", so we can echo it back to them. Returns null if it can't resolve.
function describeTimeZone() {
  try {
    const now = new Date()
    const part = (style) =>
      new Intl.DateTimeFormat(undefined, { timeZoneName: style })
        .formatToParts(now)
        .find((p) => p.type === 'timeZoneName')?.value || null
    const long = part('long')
    const short = part('short')
    if (long && short && short !== long) return `${long} (${short})`
    return long || short || null
  } catch {
    return null
  }
}

export default function BookPage() {
  const { isDark } = useDarkMode()
  const calTheme = isDark ? 'dark' : 'light'
  const calConfig = useMemo(
    () => ({ layout: 'month_view', theme: calTheme, hideBranding: 'true' }),
    [calTheme],
  )

  // The booker auto-detects the visitor's timezone; we echo it back so a wrong
  // device clock is obvious before booking. Resolved on the client only – the
  // prerendered intro must not bake in the build server's zone.
  const [timeZoneLabel, setTimeZoneLabel] = useState(null)
  useEffect(() => {
    setTimeZoneLabel(describeTimeZone())
  }, [])

  useEffect(() => {
    // Cal touches `window`, so configure it only after the client mounts. The
    // booking page is client-only (the prerender ships a static intro), so this
    // never runs during SSR.
    let cancelled = false
    ;(async () => {
      const cal = await getCalApi()
      if (cancelled) return
      cal('ui', {
        ...calConfig,
        hideEventTypeDetails: false,
        cssVarsPerTheme: CAL_CSS_VARS,
      })
    })()
    return () => {
      cancelled = true
    }
  }, [calConfig])

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <Seo path="/book" />
      <SiteHeader />

      <main className="flex-1">
        <div className="relative">
          <PublicBirds className="hidden sm:block" />
          <div className="relative mx-auto w-full max-w-4xl px-5 pb-8 pt-12 sm:pt-16">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Book a call
            </p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              Talk through your plan with us
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-muted-foreground">
              Pick a 30-minute slot to talk through your plan with the Retira founders. Bring questions or a plan
              you&apos;re working on. You&apos;ll only see times when we&apos;re free.
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-4xl px-5 pb-10">

        {timeZoneLabel ? (
          <p className="mb-3 inline-flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span>
              We detected your time zone as {timeZoneLabel}. Not right? Change it in the calendar&apos;s
              time zone menu.
            </span>
          </p>
        ) : null}

        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <Cal
            key={calTheme}
            calLink={CAL_LINK}
            style={{ width: '100%', height: '100%', minHeight: '640px', overflow: 'scroll' }}
            config={calConfig}
          />
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          Calendar not loading?{' '}
          <a
            href={`https://cal.com/${CAL_LINK}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-foreground underline underline-offset-4 hover:text-foreground/80"
          >
            Open the booking page directly
          </a>{' '}
          or email{' '}
          <a
            href="mailto:hello@retira.app"
            className="font-medium text-foreground underline underline-offset-4 hover:text-foreground/80"
          >
            hello@retira.app
          </a>
          .
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  )
}
